import React, {memo, ReactElement, useCallback, useEffect, useMemo} from "react";
import {IFilterItem, Props} from "./Filter.types";
import {hasFiltersChanged, initialState, reducer} from "./Filter.reducer";
import "./Filter.styles.scss";
import {AcceptIcon, Button, Checkbox, Flex, Input, MenuButton, MenuItemProps, Text} from "@fluentui/react-northstar";
import {ShorthandCollection, ShorthandValue} from "@fluentui/react-northstar/dist/es/types";
import {Immutable, useMagicReducer} from "@witivio_teamspro/use-reducer";
import translations from "../../../translations";
import {CompareModule} from "../../../modules/Compare.module";

export const Filter = memo((props: Props): ReactElement | null => {
    const [state, dispatch] = useMagicReducer(reducer(props), initialState);

    useEffect(function onItemsChange() {
        dispatch("setDefaultItems")(props.items);
        dispatch("reset", true)();
        if (state.data) props.onItemChecked(state.data);
    }, [props.items]);

    useEffect(function handleCloseOnOutsideClick() {
        if (!state.open || !props.closeOnClickItem) return;
        window.document.body.addEventListener("click", dispatch("close"));
        return () => window.document.body.removeEventListener("click", dispatch("close"));
    }, [state.open]);

    const isSmallScreen = window.innerWidth <= 750;

    const formatItemWithMenu = useCallback((item: Immutable<IFilterItem>) => {
        if (!item.menu) throw new Error("Item don't have a menu");
        const formattedItem: ShorthandValue<MenuItemProps> = {key: item.key};
        formattedItem.content = item.label;
        formattedItem.icon = item.icon;
        formattedItem.onClick = stopPropagation;
        formattedItem.on = "hover";
        formattedItem.menu = {
            items: item.menu.items.map(subItem => formatItem(subItem, item)),
            popper: {position: props.enforceOpenBeforeMenu ? "before" : "after", offset: [-13, 0]},
            ...(isSmallScreen && {
                popper: {align: "end", position: "below"}
            }),
            onClick: stopPropagation,
        };
        return formattedItem;
    }, []);

    const formatSingleItem = useCallback((item: Immutable<IFilterItem>, parentItem?: Immutable<IFilterItem>) => {
        const formattedItem: ShorthandValue<MenuItemProps> = {key: item.key};
        const unitWidth = !!item.input?.unit ? item.input?.unit.width : 0;
        const inputMaxWidth = unitWidth + 80;
        const isParentUniqueMenu = parentItem?.menu?.unique;
        formattedItem.onClick = dispatch("handleToggleItem", [item.key, parentItem?.key]);
        formattedItem.children = (
            <Flex fill gap={"gap.small"} space={"between"} vAlign={"center"}>
                <Flex gap={"gap.small"} vAlign={"center"}>
                    {item.icon}
                    <Text content={item.label}/>
                </Flex>
                {!item.input ? null :
                    <Flex
                        fill className={"filter-input-container"}
                        styles={{maxWidth: inputMaxWidth + "px"}}
                        onClick={stopPropagation}>
                        <Input
                            fluid
                            value={item.input.value}
                            type={item.input.type ?? "text"}
                            min={item.input.min}
                            max={item.input.max}
                            input={{styles: {paddingRight: unitWidth + "px"}}}
                            className={"filter-input"}
                            icon={<Text styles={{color: "grey"}} content={item.input.unit.label}/>}
                            onChange={dispatch("handleFilterItemInputValueChange", item.key)}
                        />
                    </Flex>
                }
                {item.input?.hideCheckbox ? null :
                    isParentUniqueMenu ?
                        <AcceptIcon className={"unique-menu-item-check " + (item.isChecked ? "checked" : "")}/>
                        :
                        <Checkbox labelPosition={"start"} checked={item.isChecked ?? false}/>
                }
            </Flex>
        )
        return formattedItem;
    }, []);

    const formatItem = useCallback((item: Immutable<IFilterItem>, parentItem?: Immutable<IFilterItem>) => {
        if (!!item.menu) return formatItemWithMenu(item);
        return formatSingleItem(item, parentItem);
    }, []);

    const formattedItems = useMemo((): ShorthandCollection<MenuItemProps> => {
        if (state.items.length === 0) return [];
        const items: ShorthandCollection<MenuItemProps> = [
            {
                key: "header",
                disabled: true,
                className: "filter-header",
                children: (
                    <Flex fill gap={"gap.small"} space={"between"} vAlign={"center"}>
                        <Text className={"header-title"} weight={"semibold"}
                              content={translations.translate("Filters")}/>
                        <Button styles={{minWidth: "0px"}} primary text content={translations.translate("Reset")}
                                onClick={dispatch("handleResetFilters")}/>
                    </Flex>
                ),
            },
            {
                key: "divider-1",
                kind: "divider",
                styles: {margin: "5px 0"}
            },
        ];
        items.push(...state.items.map(item => formatItem(item)));
        return items;
    }, [state.items]);

    const isButtonPrimary = hasFiltersChanged(state.defaultItems, state.items);

    const trigger = (
        <Flex onClick={dispatch("toggleOpen")}
              {...(props.on === "hover" && {onMouseEnter: dispatch("open")})}>
            {props.renderTrigger(isButtonPrimary)}
        </Flex>
    )

    return (
        <Flex className={"filter"}>
            <MenuButton
                className={"no-select"}
                open={state.open}
                mouseLeaveDelay={500}
                onOpenChange={dispatch("handlePopupHoverOpenChange")}
                onClick={stopPropagation}
                onMouseEnter={persistEvent}
                onMouseLeave={persistEvent}
                onMouseDown={persistEvent}
                onMouseUp={persistEvent}
                trigger={trigger}
                on={"hover"}
                align={"start"}
                menu={formattedItems}
            />
        </Flex>
    )
}, (prevProps, nextProps) => (
    CompareModule.areObjectsEqual(prevProps.items, nextProps.items, true) &&
    prevProps.onItemChecked === nextProps.onItemChecked &&
    prevProps.disabled === nextProps.disabled
));

///////////////////////////////////////////////////// PURE METHODS /////////////////////////////////////////////////////

const stopPropagation = (e: React.SyntheticEvent) => {
    e.persist();
    e.stopPropagation();
}

const persistEvent = (e: React.SyntheticEvent) => {
    e.persist();
}